import { DateTime, Interval } from 'luxon';
import React, { createContext, useState } from 'react';
import { IntervalType } from '../../types/IntervalType';
import { getInterval } from '../../utils/functions';

export interface ChartInterval {
    type: IntervalType;
    startDate?: DateTime;
    endDate?: DateTime;
    interval: Interval;
}

export interface ChartIntervalContextType {
    isLoading: boolean;
    selected: ChartInterval;
    select: (interval: IntervalType, custom?: { startDate: DateTime; endDate: DateTime }) => void;
}

export const ChartIntervalContext = createContext<ChartIntervalContextType>({
    isLoading: true,
    selected: {
        type: '5Y',
        startDate: undefined,
        endDate: undefined,
        interval: getInterval('5Y'),
    },
    select: undefined!,
});

export const WithChartInterval: React.FC<{children?: React.ReactNode}> = ({ children }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [chartInterval, setChartInterval] = useState<ChartInterval>({
        type: '5Y',
        startDate: undefined,
        endDate: undefined,
        interval: getInterval('5Y'),
    });

    return (
        <ChartIntervalContext.Provider
            value={{
                isLoading,
                selected: chartInterval,
                select: (selection, custom) => {
                    setIsLoading(false);
                    if (selection === 'Custom' && !custom) {
                        // No custom dates while type is custom, so fallback
                        setChartInterval({
                            type: '5Y',
                            startDate: undefined,
                            endDate: undefined,
                            interval: getInterval('5Y'),
                        });
                    }

                    const interval =
                        selection === 'Custom'
                            ? Interval.fromDateTimes(custom!.startDate, custom!.endDate)
                            : getInterval(selection);

                    setChartInterval({
                        type: selection,
                        startDate: custom?.startDate,
                        endDate: custom?.endDate,
                        interval,
                    });
                },
            }}
        >
            {children}
        </ChartIntervalContext.Provider>
    );
};
